import { useState } from "react";
import { toast } from "sonner";
import { Upload, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { PageHero } from "@/components/common";

export default function Reporta() {
  const [form, setForm] = useState({ title: "", description: "", category: "", agency: "", location: "", anonymous: true });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 4 || form.description.trim().length < 10) {
      toast.error("Añade un título y una descripción más completa."); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("country", "puerto-rico");
      if (file) fd.append("attachment", file);
      const r = await api.post("/reports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.message);
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el reporte.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="reporta-page">
      <PageHero label="Reporta" title="Comparte información con el equipo"
        subtitle="Tu reporte llega a una bandeja privada. Nada se publica automáticamente: el equipo lo revisa, clasifica y verifica." />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 flex gap-3 mb-8">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">No enviemos información que ponga en riesgo tu seguridad. Puedes reportar de forma anónima; aun así, evita incluir datos personales sensibles en los archivos. 2Voto no promete anonimato absoluto.</p>
        </div>

        {done ? (
          <div className="bg-white rounded-lg border border-slate-200 p-10 text-center" data-testid="report-success">
            <div className="w-14 h-14 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-4">
              <Upload className="w-6 h-6 text-cyan-600" />
            </div>
            <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">Reporte recibido</h3>
            <p className="text-slate-600">Gracias. El equipo editorial lo revisará antes de cualquier acción o publicación.</p>
            <button onClick={() => { setDone(false); setForm({ title: "", description: "", category: "", agency: "", location: "", anonymous: true }); setFile(null); }}
              className="mt-6 text-cyan-600 font-semibold" data-testid="report-another">Enviar otro reporte</button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-lg border border-slate-200 p-6 space-y-5" data-testid="report-form">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Título *</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="report-title"
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-cyan-500" placeholder="Resumen breve del asunto" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Descripción *</label>
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="report-description" rows={5}
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-cyan-500" placeholder="¿Qué ocurrió? ¿Cuándo y dónde?" />
              <p className="text-xs text-slate-400 mt-1">Mínimo 10 caracteres. Incluye fechas, lugares y datos verificables si los tienes.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Categoría</label>
                <input value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="report-category"
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-cyan-500" placeholder="Ej. Contratos, Ambiente" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Agencia o entidad</label>
                <input value={form.agency} onChange={(e) => set("agency", e.target.value)} data-testid="report-agency"
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-cyan-500" placeholder="Opcional" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ubicación</label>
              <input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="report-location"
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-cyan-500" placeholder="Municipio, dirección aproximada (opcional)" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Adjunto (opcional, máx 8MB)</label>
              <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="report-file"
                className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-slate-100 file:text-slate-700 file:font-medium" />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.anonymous} onChange={(e) => set("anonymous", e.target.checked)} data-testid="report-anonymous" className="w-4 h-4 accent-cyan-500" />
              <span className="text-sm text-slate-700">Enviar de forma anónima</span>
            </label>
            <button type="submit" disabled={busy} data-testid="report-submit"
              className="w-full bg-rose-500 text-white font-semibold py-3 rounded-full hover:bg-rose-600 transition-colors disabled:opacity-60">
              {busy ? "Enviando…" : "Enviar reporte"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
